import {
  AdditionalLegInput,
  OrderGroupBuildDetailInput,
  ShipmentType,
} from "@api/graphql/generated/generated-types";

// The dispatcher's own choices for the way back. null = same as the way out.
export interface ReturnLegFormValues {
  packageTypeId: string | null;
  packageCount: number | null;
  serviceTypeId: string | null;
}

// The way out as the form has it. null = no single value (e.g. mixed package rows).
export interface OutboundLegValues {
  packageTypeId: string | null;
  packageCount: number | null;
  serviceTypeId: string | null;
}

export const EMPTY_RETURN_LEG: ReturnLegFormValues = {
  packageTypeId: null,
  packageCount: null,
  serviceTypeId: null,
};

export function isReturnLegEmpty(returnLeg: ReturnLegFormValues | undefined | null): boolean {
  return returnLeg?.packageTypeId == null && returnLeg?.packageCount == null && returnLeg?.serviceTypeId == null;
}

// Cube splits only a single drop-off round trip, and only one-off orders can carry the detail.
export function isReturnTripCardShown({
  shipmentType,
  isOneOff,
  builderId,
}: {
  shipmentType: string | null | undefined;
  isOneOff: boolean;
  builderId: string | null;
}): boolean {
  return shipmentType === ShipmentType.RoundTrip && isOneOff && builderId != null;
}

// Untouched fields go as null: cube then copies the way out exactly, including package rows,
// weight and size, which it drops as soon as the return sends its own package type or count.
export function makeReturnLegForSubmit(returnLeg: ReturnLegFormValues | undefined | null): AdditionalLegInput {
  return {
    packageTypeId: returnLeg?.packageTypeId ?? null,
    packageCount: returnLeg?.packageCount ?? null,
    serviceTypeId: returnLeg?.serviceTypeId ?? null,
  };
}

export function makeOrderGroupBuildDetail({
  shipmentType,
  isOneOff,
  builderId,
  returnLeg,
}: {
  shipmentType: string | null | undefined;
  isOneOff: boolean;
  builderId: string | null;
  returnLeg: ReturnLegFormValues | undefined | null;
}): OrderGroupBuildDetailInput | undefined {
  if (!builderId || !isReturnTripCardShown({ shipmentType, isOneOff, builderId })) return undefined;
  return { orderGroupBuilderId: builderId, additionalLegs: [makeReturnLegForSubmit(returnLeg)] };
}

// With mixed way-out rows there is no type for a bare quantity to go with: cube drops the way-out
// rows once the return sets its own packages, so a count alone would leave no package type.
export function isReturnQuantityEnabled(returnLeg: ReturnLegFormValues, outbound: OutboundLegValues): boolean {
  return outbound.packageTypeId != null || returnLeg.packageTypeId != null;
}

// The customer-filtered list the main picker loaded, minus services that don't allow the booked
// vehicle (cube rejects those for the return leg). No pickup-time filter: dispatchers may book any
// service at any time.
export function filterReturnServiceTypes<T extends { id: string; visibleVehicleTypeIds?: string[] | null }>(
  serviceTypes: T[] | undefined,
  vehicleTypeId: string | null | undefined
): T[] {
  return (serviceTypes ?? []).filter((serviceType) => {
    const allowedVehicleTypeIds: string[] = serviceType.visibleVehicleTypeIds ?? [];
    return !vehicleTypeId || allowedVehicleTypeIds.length === 0 || allowedVehicleTypeIds.includes(vehicleTypeId);
  });
}

// An empty or invalid box means "same as the way out" (null); cube rejects counts below 1.
export function parsePackageCount(text: string): number | null {
  if (!/^\d+$/.test(text.trim())) return null;
  const packageCount: number = Number(text.trim());
  return packageCount >= 1 ? packageCount : null;
}

// The loaded service list belongs to the customer it was fetched for; mid-switch it's the old one.
export function areServiceTypesLoadedForCustomer(
  loadedForEndCustomerId: string | null | undefined,
  hasLoaded: boolean,
  selectedCustomerId: string | null | undefined
): boolean {
  return hasLoaded && (loadedForEndCustomerId ?? null) === (selectedCustomerId ?? null);
}
